/**
 * replayAdapter — converts the DB-backed `GET /api/debate/history/{id}`
 * replay payload (`DebateReplayData`) into the SAME typed shapes the live
 * SSE pipeline builds (`Persona[]`, `Turn[]`, `Verdict`).
 *
 * Two consumers:
 *   1. `ReplayPage.tsx` — feeds `openVerdictReport` (PDF export) for a
 *      TERMINAL (finalized/interrupted) session. Purely presentational,
 *      never touches the live Zustand store.
 *   2. `sessionSlice.ts`'s `resume()` action — İş Kolu "arka planda devam
 *      eden tartışma": re-hydrates the LIVE store from a RUNNING session's
 *      persisted transcript-so-far, then attaches a live SSE tail via
 *      `GET /api/debate/{id}/stream` to keep receiving new turns. This is
 *      what lets a user close the tab mid-debate and pick the SAME
 *      interactive roundtable view back up later.
 *
 * Both consumers need byte-identical persona/turn/verdict shaping — this
 * module is the single source of truth so they can never drift.
 */
import type {
  AgentRole,
  Critique,
  CritiqueSeverity,
  DebateTopology,
  EvidenceBibliographyEntry,
  Persona,
  Turn,
  TurnPhase,
  Verdict,
} from "./types";
import { coerceActionPlan } from "./types";
import type { DebateReplayData } from "../transport/authClient";
import { assignUniqueAvatars } from "../utils/avatarPool";

export function severityCanonical(s?: string): CritiqueSeverity {
  const v = (s ?? "minor").toLowerCase();
  if (v === "blocker") return "blocker";
  if (v === "major") return "major";
  return "minor";
}

/* ── Gevşek replay payload'larını tipli Verdict alanlarına indirger ──
   `exactOptionalPropertyTypes` açık olduğu için opsiyonel anahtarlar
   `undefined` ATANMAZ; veri yoksa anahtar hiç eklenmez. */

type Loose = Record<string, unknown>;

const rStr = (v: unknown): string | undefined =>
  typeof v === "string" && v.trim() ? v.trim() : undefined;

const rStrings = (v: unknown): string[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const out = v.filter((x): x is string => typeof x === "string" && x.trim() !== "");
  return out.length > 0 ? out : undefined;
};

const rNum = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined;

export function coerceBluf(raw: unknown): NonNullable<Verdict["bluf"]> | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Loose;
  const out: NonNullable<Verdict["bluf"]> = {};
  const rec = rStr(o.recommendation);
  if (rec) out.recommendation = rec;
  const reasons = rStrings(o.reasons);
  if (reasons) out.reasons = reasons;
  const mainRisk = rStr(o.mainRisk ?? o.main_risk);
  if (mainRisk) out.mainRisk = mainRisk;
  const conf = rNum(o.confidence);
  if (conf !== undefined) out.confidence = conf;
  const need = rStrings(o.needToKnow ?? o.need_to_know);
  if (need) out.needToKnow = need;
  const rev = rStrings(o.reversalConditions ?? o.reversal_conditions);
  if (rev) out.reversalConditions = rev;
  return Object.keys(out).length > 0 ? out : null;
}

export function coerceVerdictStatus(
  raw: unknown,
): NonNullable<Verdict["verdictStatus"]> | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Loose;
  const out: NonNullable<Verdict["verdictStatus"]> = {};
  const state = rStr(o.state);
  if (
    state === "answered" ||
    state === "abstained" ||
    state === "degraded" ||
    state === "review_required"
  ) {
    out.state = state;
  }
  const reasons = rStrings(o.reasons);
  if (reasons) out.reasons = reasons;
  const reviewRequired = o.reviewRequired ?? o.review_required;
  if (typeof reviewRequired === "boolean") out.reviewRequired = reviewRequired;
  const signals = o.signals;
  if (signals && typeof signals === "object" && !Array.isArray(signals)) {
    out.signals = signals as Loose;
  }
  const hrRaw = o.humanReview ?? o.human_review;
  if (hrRaw && typeof hrRaw === "object") {
    const h = hrRaw as Loose;
    const hr: NonNullable<NonNullable<Verdict["verdictStatus"]>["humanReview"]> = {};
    const dec = rStr(h.decision);
    if (dec === "approve" || dec === "conditional" || dec === "reject") {
      hr.decision = dec;
    }
    const note = rStr(h.note);
    if (note) hr.note = note;
    const conds = rStrings(h.conditions);
    if (conds) hr.conditions = conds;
    const at = rNum(h.reviewedAt ?? h.reviewed_at);
    if (at !== undefined) hr.reviewedAt = at;
    if (Object.keys(hr).length > 0) out.humanReview = hr;
  }
  return Object.keys(out).length > 0 ? out : null;
}

export function coerceEvidenceBibliography(
  raw: unknown,
): EvidenceBibliographyEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: EvidenceBibliographyEntry[] = [];
  for (const it of raw) {
    if (!it || typeof it !== "object") continue;
    const o = it as Loose;
    const name = rStr(o.name) ?? rStr(o.url);
    if (!name) continue;
    out.push({
      name,
      url: rStr(o.url) ?? "",
      citedByTurnCount:
        rNum(o.citedByTurnCount ?? o.cited_by_turn_count) ?? 0,
      citedBySpeakerIds:
        rStrings(o.citedBySpeakerIds ?? o.cited_by_speaker_ids) ?? [],
    });
  }
  return out;
}

export interface ReplayCoreAdaptation {
  query: string;
  topology: DebateTopology;
  startedAt: number | null;
  status: string;
  maxRounds: number | null;
  verdict: Verdict | null;
  personas: Persona[];
  turns: Turn[];
  /** 0..1 scalar — last-known consensus score, or undefined if never scored. */
  consensusScore?: number;
}

/**
 * Core persona/turn/verdict adaptation — NO metrics/bibliography
 * derivation (that's `ReplayPage`-only, PDF-specific, and stays there).
 * Kept minimal so the live-resume path (`sessionSlice.resume`) pays no
 * cost for PDF-only computation.
 */
export function adaptReplayCore(data: DebateReplayData): ReplayCoreAdaptation {
  const sess = data.session;
  const v = data.verdict;

  const verdict: Verdict | null = v
    ? {
        executiveSummary: v.executive ?? "",
        dissentingLog: (v.dissenting ?? []).map((d) => ({
          agentId: String(d.agentId ?? ""),
          concern: String(d.concern ?? ""),
          severity: severityCanonical(d.severity),
        })),
        actionPlan: coerceActionPlan(v.action_plan ?? {}),
        generatedAt: sess.ended_at ? new Date(sess.ended_at).getTime() : Date.now(),
        simulationScore: v.simulation_score ?? null,
        simulationOutcomes: (v.simulation_outcomes ?? []).map((o) => ({
          scenario: String(o.scenario ?? ""),
          survived: o.survived === true,
          rationale: o.rationale ? String(o.rationale) : "",
        })),
        commentaryAnalysis: v.commentary_analysis ?? null,
        commentaryCritique: v.commentary_critique ?? null,
        // Görev 18-24 + Karar Paketi. Bu blok eksikti: replay'den açılan
        // rapor BLUF'u, güven skorunu, onay kapısını, karar paketini ve
        // kapsam artefaktlarını hiç görmüyordu. Şekiller gevşek tipli
        // olduğu için doğrudan taşınır; okuyucular savunmacıdır.
        baselineSimulationScore: v.baseline_simulation_score ?? null,
        hardenedSimulationScore: v.hardened_simulation_score ?? null,
        simulationRuns: v.simulation_runs ?? [],
        resilienceStatus: v.resilience_status ?? null,
        hardenedActionPlan: v.hardened_action_plan
          ? coerceActionPlan(v.hardened_action_plan)
          : null,
        confidenceMetrics: v.confidence_metrics ?? null,
        boardBrief: v.board_brief ?? null,
        decisionMemory: v.decision_memory ?? null,
        bluf: coerceBluf(v.bluf),
        decisionManifest: v.decision_manifest ?? null,
        verdictStatus: coerceVerdictStatus(v.verdict_status),
        decisionType: v.decision_type ?? null,
        evidenceConflicts: v.evidence_conflicts ?? [],
        evidenceBibliography: coerceEvidenceBibliography(v.evidence_bibliography),
        evidenceQuality:
          v.evidence_quality === "degraded" || v.evidence_quality === "ok"
            ? v.evidence_quality
            : null,
        coverage_matrix: v.coverage_matrix ?? null,
        entity_graph: v.entity_graph ?? null,
        consistency_flags: v.consistency_flags ?? null,
        investigation_plan: v.investigation_plan ?? null,
      }
    : null;

  // Build personas first so the debate-scoped unique-avatar allocator can
  // run over the full council at once (uniqueness is a global property).
  const personasDraft: Persona[] = data.personas.map((p, i) => {
    const honorific: "Bey" | "Hanım" | undefined =
      p.honorific === "Hanım" || p.honorific === "Bey" ? p.honorific : undefined;
    const gender: "male" | "female" | undefined =
      p.gender === "female" || p.gender === "male"
        ? p.gender
        : honorific === "Hanım"
          ? "female"
          : "male";
    return {
      id: p.id,
      role: (p.role as AgentRole) || "strategic-visionary",
      displayName: p.display_name,
      expertise: p.expertise ?? "",
      systemInstruction: "",
      asymmetry: { sources: [], blindSpots: [], confidenceCeiling: 1 },
      voiceId: p.voice_id ?? "",
      themeColor: p.theme_color ?? "#6366f1",
      seatIndex: i,
      ...(honorific ? { honorific } : {}),
      ...(gender ? { gender } : {}),
      ...(p.avatar_url ? { avatarUrl: p.avatar_url } : {}),
    };
  });
  const allocation = assignUniqueAvatars(personasDraft);
  const personas: Persona[] = personasDraft.map((p) => {
    const url = allocation.get(p.id);
    return url ? { ...p, avatarUrl: url } : p;
  });

  const turns: Turn[] = data.turns.map((t) => {
    const critiqueRaw = t.critique as
      | { targetTurnId?: string; flaw?: string; severity?: string }
      | null
      | undefined;
    const critique: Critique | null = critiqueRaw
      ? {
          targetTurnId: String(critiqueRaw.targetTurnId ?? ""),
          flaw: String(critiqueRaw.flaw ?? ""),
          severity: severityCanonical(critiqueRaw.severity),
        }
      : null;
    return {
      id: t.id,
      speakerId: t.speaker_id,
      roundIndex: t.round_index,
      phase: (t.phase as TurnPhase) || "yielding",
      critique,
      tokens: [],
      finalText: t.final_text,
      startedAt: new Date(t.started_at).getTime(),
      endedAt: t.ended_at ? new Date(t.ended_at).getTime() : null,
      audioChunkIds: [],
      outcome: t.final_text || (t.char_count ?? 0) > 0 ? "spoke" : "forfeited",
    };
  });

  // session.consensus_score is 0-100; the live store's ConsensusSnapshot
  // wants a 0..1 scalar (matches `consensus.update`'s `snapshot.scalarScore`).
  const consensusScalar =
    sess.consensus_score != null ? sess.consensus_score / 100 : undefined;

  const result: ReplayCoreAdaptation = {
    query: sess.query,
    topology: (sess.topology as DebateTopology) || "round-robin",
    startedAt: sess.started_at ? new Date(sess.started_at).getTime() : null,
    status: sess.status,
    maxRounds: sess.max_rounds ?? null,
    verdict,
    personas,
    turns,
  };
  if (consensusScalar !== undefined) {
    result.consensusScore = consensusScalar;
  }
  return result;
}
